import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Globe, ArrowLeft, ExternalLink, Copy, Pencil } from 'lucide-react';
import { Container, PageHeader } from '@vnetwork/ui/layout';
import {
  Card,
  CardHeader,
  CardTitle,
  CardBody,
  Badge,
  Skeleton,
  IconButton,
  Switch,
  Button,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '@vnetwork/ui/primitives';
import { Alert, ConfirmDialog, useToast } from '@vnetwork/ui/feedback';
import {
  useWafDomainDetail,
  useUpdateWafDomainSettings,
  useSetWafEarlyHint,
  useSetWafOcsp,
  useSetWafEnabled,
  useValidateWafFallback,
  useWafTags,
  useWafCustomPages,
  fmtEpoch,
  wafExtractError,
  type WafDomainDetail as Detail,
  type WafCustomPageUpdate,
} from '@/api/waf';
import { trackFeatureAction } from '@/lib/analytics-events';
import { TagEditor } from './TagEditor';
import { OriginAddressCard } from './OriginAddressCard';
import { cap, fmtStatus, sslTypeLabel } from './format';
import styles from './WafDomainDetail.module.css';

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <>
      <div style={{ color: 'var(--vn-fg-muted)' }}>{label}</div>
      <div style={{ wordBreak: 'break-word' }}>{value ?? '—'}</div>
    </>
  );
}

const yesNo = (v: boolean) =>
  v ? (
    <Badge tone="success" appearance="subtle" size="sm">Enabled</Badge>
  ) : (
    <Badge tone="neutral" appearance="subtle" size="sm">Disabled</Badge>
  );


/** Content-compression modes (vendor `compressType`). */
const COMPRESS_TYPE_OPTIONS = [
  { value: 'gzip', label: 'Gzip' },
  { value: 'gzip_brotli', label: 'Gzip + Brotli' },
  { value: 'off', label: 'Off' },
];

/** A `Row` whose value is an interactive on/off Switch. */
function ToggleRow({
  label,
  checked,
  pending,
  onChange,
}: {
  label: string;
  checked: boolean;
  pending: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <Row
      label={label}
      value={<Switch checked={checked} disabled={pending} onCheckedChange={onChange} />}
    />
  );
}

// Custom-page assignments. Each event picks from the customer's custom-page
// catalog (`GET /waf/custom-pages`) filtered by the matching vendor `type`; the
// option value is the page id (0 = DEFAULT). The vendor replaces the whole
// customPage block, so each change submits all fields together.
type CpNumKey = 'block' | 'error_5xx' | 'rate_limit' | 'js' | 'captcha';
const PAGE_DEFAULT = { label: 'DEFAULT', value: 0 };
// Field key → the custom-page `type` token whose pages populate that select.
const CUSTOM_PAGE_FIELDS: { key: CpNumKey; label: string; type: string }[] = [
  { key: 'block', label: 'Block', type: 'block' },
  { key: 'error_5xx', label: '500-Class error', type: '5xx' },
  { key: 'rate_limit', label: 'Rate limit', type: 'rateLimit' },
  { key: 'js', label: 'JS challenge', type: 'js' },
  { key: 'captcha', label: 'Captcha', type: 'captcha' },
];

const toCustomPageUpdate = (cp: Detail['custom_page']): WafCustomPageUpdate => ({
  block: cp.block,
  error_5xx: cp.error_5xx,
  rate_limit: cp.rate_limit,
  js: cp.js,
  captcha: cp.captcha,
  maint_enable: cp.maint_enable,
  maint_page_id: cp.maint_page_id ?? 0,
});

/** A 2-column form cell: label stacked above its control. */
function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 0 }}>
      <span style={{ color: 'var(--vn-fg-muted)', fontSize: 'var(--vn-font-size-sm)' }}>{label}</span>
      {children}
    </div>
  );
}

function CustomPageSection({
  serviceId,
  cp,
  pending,
  onChange,
}: {
  serviceId: string;
  cp: Detail['custom_page'];
  pending: boolean;
  onChange: (next: WafCustomPageUpdate) => void;
}) {
  // Each change re-sends the full block (vendor replaces it wholesale).
  const apply = (patch: Partial<WafCustomPageUpdate>) =>
    onChange({ ...toCustomPageUpdate(cp), ...patch });

  // Custom-page catalog → options grouped by the event `type`.
  const { data: cpData, isLoading: pagesLoading } = useWafCustomPages(serviceId, { page_size: 100 });
  const pages = cpData?.items ?? [];
  // DEFAULT + pages of the given type, plus a fallback so a currently-assigned
  // id that isn't (yet) in the catalog still shows instead of rendering blank.
  const optionsFor = (type: string, current: number) => {
    const opts = [
      PAGE_DEFAULT,
      ...pages.filter((p) => p.type === type).map((p) => ({ label: p.name, value: p.id })),
    ];
    if (current && !opts.some((o) => o.value === current)) {
      opts.push({ label: `#${current}`, value: current });
    }
    return opts;
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Custom page</CardTitle>
      </CardHeader>
      <CardBody>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
            gap: 'var(--vn-space-3) var(--vn-space-4)',
          }}
        >
          {CUSTOM_PAGE_FIELDS.map((f) => (
            <Field key={f.key} label={f.label}>
              <Select
                value={String(cp[f.key] ?? 0)}
                disabled={pending || pagesLoading}
                onValueChange={(v) => apply({ [f.key]: Number(v) } as Partial<WafCustomPageUpdate>)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {optionsFor(f.type, cp[f.key] ?? 0).map((o) => (
                    <SelectItem key={o.value} value={String(o.value)}>
                      {o.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
          ))}

          {/* Maintenance: a label + inline toggle, with the page picker below.
              A non-DEFAULT page must be picked before maintenance can be enabled;
              switching the page back to DEFAULT turns maintenance off. */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, minWidth: 0 }}>
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--vn-space-2)' }}>
              <span style={{ color: 'var(--vn-fg-muted)', fontSize: 'var(--vn-font-size-sm)' }}>Maintenance</span>
              <Switch
                checked={cp.maint_enable}
                // Can't enable without a real (non-DEFAULT) maintenance page.
                disabled={pending || (!cp.maint_enable && !cp.maint_page_id)}
                onCheckedChange={(v) => apply({ maint_enable: v })}
                aria-label="Maintenance"
              />
            </div>
            <Select
              value={String(cp.maint_page_id ?? 0)}
              disabled={pending || pagesLoading}
              onValueChange={(v) => {
                const id = Number(v);
                // Selecting DEFAULT (0) also turns maintenance off.
                apply(id ? { maint_page_id: id } : { maint_page_id: 0, maint_enable: false });
              }}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select" />
              </SelectTrigger>
              <SelectContent>
                {optionsFor('maint', cp.maint_page_id ?? 0).map((o) => (
                  <SelectItem key={o.value} value={String(o.value)}>
                    {o.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {!cp.maint_page_id && (
              <span style={{ color: 'var(--vn-fg-subtle)', fontSize: 'var(--vn-font-size-sm)' }}>
                Select a maintenance page to enable.
              </span>
            )}
          </div>
        </div>
      </CardBody>
    </Card>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>{title}</CardTitle>
      </CardHeader>
      <CardBody>
        <div className={styles.detailGrid}>{children}</div>
      </CardBody>
    </Card>
  );
}

export function WafDomainDetail({ serviceId }: { serviceId: string }) {
  const navigate = useNavigate();
  const { show } = useToast();
  const [params] = useSearchParams();
  const domain = params.get('domain') ?? '';
  const base = `/services/waf/${serviceId}`;

  const copyCname = (cname: string) => {
    void navigator.clipboard.writeText(cname);
    show({ tone: 'success', title: 'CNAME copied' });
  };

  const { data, isLoading, isError, refetch } = useWafDomainDetail(serviceId, domain);

  const ok = () => show({ tone: 'success', title: 'Updated' });
  const fail = (e: unknown) =>
    show({ tone: 'danger', title: wafExtractError(e, 'Update failed') });

  const settingsMut = useUpdateWafDomainSettings(serviceId, domain, {
    onSuccess: () => { ok(); trackFeatureAction('waf', 'domain', 'edit'); },
    onError: fail,
  });
  const earlyHintMut = useSetWafEarlyHint(serviceId, domain, { onSuccess: ok, onError: fail });
  const ocspMut = useSetWafOcsp(serviceId, domain, { onSuccess: ok, onError: fail });
  const wafMut = useSetWafEnabled(serviceId, domain, {
    onSuccess: (_data: unknown, enabled: boolean) => { ok(); trackFeatureAction('waf', 'domain', enabled ? 'enable' : 'disable'); },
    onError: fail,
  });
  const validateMut = useValidateWafFallback(serviceId, domain);

  const { data: tagOptions } = useWafTags(serviceId);
  const [editingTags, setEditingTags] = useState(false);

  // Origin Shield = WAF edge. Changing it runs validate-fallback first; if the
  // switch would route traffic to the origin (fallback), confirm before applying.
  const [pendingShield, setPendingShield] = useState<boolean | null>(null);
  const shieldBusy = validateMut.isPending || wafMut.isPending;
  const onShieldChange = (next: boolean) => {
    if (next === data?.waf_enabled) return;
    validateMut.mutate(next, {
      onSuccess: (isFallback) => {
        if (isFallback) setPendingShield(next);
        else wafMut.mutate(next);
      },
      onError: fail,
    });
  };

  const backBtn = (
    <button
      type="button"
      onClick={() => navigate(`${base}/domains`)}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4,
        background: 'none',
        border: 'none',
        cursor: 'pointer',
        color: 'var(--vn-fg-muted)',
        padding: 0,
      }}
    >
      <ArrowLeft size={14} /> Domains
    </button>
  );

  if (isLoading) {
    return (
      <Container size="xl">
        <Skeleton style={{ height: 64, marginBottom: 'var(--vn-space-4)' }} />
        <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} style={{ height: 160 }} />
          ))}
        </div>
      </Container>
    );
  }

  if (isError || !data) {
    return (
      <Container size="xl">
        <PageHeader icon={<Globe size={22} />} eyebrow={backBtn} title={domain || 'Domain'} />
        <Alert
          tone="danger"
          title="Couldn't load domain"
          actions={<button onClick={() => refetch()}>Retry</button>}
        >
          The configuration for this domain couldn't be loaded.
        </Alert>
      </Container>
    );
  }

  const d: Detail = data;

  return (
    <Container size="xl">
      <PageHeader
        icon={<Globe size={22} />}
        eyebrow={backBtn}
        title={
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
            {d.domain}
            <a
              href={`https://${d.domain}`}
              target="_blank"
              rel="noreferrer"
              style={{ display: 'inline-flex', color: 'var(--vn-fg-subtle)' }}
              aria-label="Open domain"
            >
              <ExternalLink size={15} />
            </a>
            <Badge tone="neutral" appearance="subtle" size="sm">{d.status ? cap(d.status) : 'Unknown'}</Badge>
          </span>
        }
        description={d.cname}
        actions={
          <span style={{ display: 'inline-flex', gap: 6, flexWrap: 'wrap' }}>
            <Badge tone={d.waf_enabled ? 'success' : 'neutral'} appearance="subtle">WAF {d.waf_enabled ? 'on' : 'off'}</Badge>
            <Badge tone={d.ssl.enable ? 'success' : 'neutral'} appearance="subtle">SSL {d.ssl.enable ? 'on' : 'off'}</Badge>
          </span>
        }
      />

      <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
        <Section title="General">
          <Row
            label="CNAME"
            value={
              d.cname ? (
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                  {d.cname}
                  <IconButton
                    variant="ghost"
                    size="sm"
                    aria-label="Copy CNAME"
                    onClick={() => copyCname(d.cname)}
                  >
                    <Copy size={13} />
                  </IconButton>
                </span>
              ) : (
                '—'
              )
            }
          />
          <Row label="Type" value={d.type || '—'} />
          <Row label="DNS status" value={fmtStatus(d.dns_status)} />
          <Row label="WAF status" value={fmtStatus(d.waf_status)} />
          <Row
            label="Origin Shield"
            value={
              <Select
                value={d.waf_enabled ? 'true' : 'false'}
                disabled={shieldBusy}
                onValueChange={(v) => onShieldChange(v === 'true')}
              >
                <SelectTrigger style={{ maxWidth: 240 }}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="true">Use Origin Shield</SelectItem>
                  <SelectItem value="false">Bypass Origin Shield</SelectItem>
                </SelectContent>
              </Select>
            }
          />
          <Row label="Locked" value={yesNo(d.locked)} />
          <Row
            label="Tags"
            value={
              editingTags ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--vn-space-2)' }}>
                  <TagEditor
                    value={d.tags ?? []}
                    options={tagOptions ?? []}
                    busy={settingsMut.isPending}
                    onChange={(next) => settingsMut.mutate({ tags: next })}
                  />
                  <div>
                    <Button size="sm" variant="ghost" onClick={() => setEditingTags(false)}>
                      Done
                    </Button>
                  </div>
                </div>
              ) : (
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                  {d.tags?.map((t) => (
                    <Badge key={t} tone="info" appearance="subtle" size="sm">
                      {t}
                    </Badge>
                  ))}
                  <Button size="sm" variant="ghost" onClick={() => setEditingTags(true)}>
                    <Pencil size={13} /> Edit tag
                  </Button>
                </span>
              )
            }
          />
          <Row label="Created" value={fmtEpoch(d.created_at)} />
          <Row label="Updated" value={fmtEpoch(d.updated_at)} />
        </Section>

        <Section title="Optimization">
          <ToggleRow
            label="HTTP/2"
            checked={d.http_version.enable_http2}
            pending={settingsMut.isPending}
            onChange={(v) => settingsMut.mutate({ enable_http2: v })}
          />
          <ToggleRow
            label="Auto-content compression"
            checked={d.auto_compression}
            pending={settingsMut.isPending}
            onChange={(v) => settingsMut.mutate({ auto_compression: v })}
          />
          <Row
            label="Compression type"
            value={
              <Select
                value={d.compress_type || 'off'}
                disabled={settingsMut.isPending}
                onValueChange={(v) => settingsMut.mutate({ compress_type: v })}
              >
                <SelectTrigger style={{ maxWidth: 240 }}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {COMPRESS_TYPE_OPTIONS.map((o) => (
                    <SelectItem key={o.value} value={o.value}>
                      {o.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            }
          />
          <ToggleRow
            label="HTTP 103 Early Hints"
            checked={d.early_hint_enable}
            pending={earlyHintMut.isPending}
            onChange={(v) => earlyHintMut.mutate(v)}
          />
        </Section>

        <OriginAddressCard
          serviceId={serviceId}
          domain={domain}
          origin={d.origin}
          globalFallback={d.global_fallback}
        />

        <Section title="SSL">
          <Row label="Enabled" value={yesNo(d.ssl.enable)} />
          <Row label="Type" value={sslTypeLabel(d.ssl.type)} />
          <Row label="Redirect to HTTPS" value={yesNo(d.ssl.redirect)} />
          <Row label="Certificate ID" value={d.ssl.cert_id || '—'} />
          <ToggleRow
            label="OCSP stapling"
            checked={d.ocsp_enable}
            pending={ocspMut.isPending || !d.ssl.enable}
            onChange={(v) => ocspMut.mutate(v)}
          />
        </Section>

        <CustomPageSection
          serviceId={serviceId}
          cp={d.custom_page}
          pending={settingsMut.isPending}
          onChange={(next) => settingsMut.mutate({ custom_page: next })}
        />

        {d.platforms?.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle>CDN Platforms ({d.platforms.length})</CardTitle>
            </CardHeader>
            <CardBody>
              <div style={{ display: 'grid', gap: 'var(--vn-space-2)' }}>
                {d.platforms.map((p) => (
                  <div
                    key={p.platform_id}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 'var(--vn-space-3)',
                      flexWrap: 'wrap',
                    }}
                  >
                    <span style={{ fontWeight: 500 }}>{p.display_name || p.name}</span>
                    {p.edge_domain && (
                      <span style={{ color: 'var(--vn-fg-muted)', fontSize: 'var(--vn-font-size-sm)' }}>
                        {p.edge_domain}
                      </span>
                    )}
                    <Badge tone={p.enable ? 'success' : 'neutral'} appearance="subtle" size="sm">
                      {p.status ? cap(p.status) : p.enable ? 'Enabled' : 'Disabled'}
                    </Badge>
                    {p.ssl && <Badge tone="info" appearance="subtle" size="sm">SSL</Badge>}
                  </div>
                ))}
              </div>
            </CardBody>
          </Card>
        )}

        <Section title="Region & CORS">
          <Row label="Region" value={d.region.region || '—'} />
          <Row label="Region type" value={d.region.region_type || '—'} />
          <Row label="Override region" value={yesNo(d.region.override_enable)} />
          <Row label="CORS" value={yesNo(d.cors.enable)} />
          <Row label="CORS origins" value={d.cors.origins?.length ? d.cors.origins.join(', ') : '—'} />
        </Section>
      </div>

      {pendingShield !== null && (
        <ConfirmDialog
          open
          onOpenChange={(o) => {
            if (!o) setPendingShield(null);
          }}
          title={pendingShield ? 'Use Origin Shield?' : 'Bypass Origin Shield?'}
          description="This change will route this domain's traffic to its origin (origin fallback). Continue?"
          confirmLabel="Continue"
          destructive
          loading={wafMut.isPending}
          onConfirm={() =>
            wafMut.mutate(pendingShield, {
              // wafMut already toasts success/error via its hook defaults; here
              // we only close the dialog once it lands.
              onSuccess: () => setPendingShield(null),
            })
          }
        />
      )}
    </Container>
  );
}

export default WafDomainDetail;
